<template>
  <Wrapper>
    <Dashboard>
      <EmailAddressStep
        v-if="step === 'email'"
        :email-address="session.emailAddress"
        :show-cancel-button="true"
        :sub-title="'ident.steps.email_address.sub_title'"
        :title="'ident.steps.email_address.title'"
        @next="handleEmailNext"
      />
      <EmailAddressVerifyStep
        v-else
        :show-dev-get-code="false"
        :sub-title="'ident.steps.email_address_verify.sub_title'"
        :title="'ident.steps.email_address_verify.title'"
        @next="handleVerifyNext"
      />
    </Dashboard>
  </Wrapper>
</template>

<script setup lang="ts">
import { toast } from 'vue-sonner'
import { Email, EmailVerify, StepNavigation } from '@/types'
import { useNaturalPersonsConfirm } from '@/composables/api/use-natural-persons-confirm.ts'
import { useNaturalPersonsEmail } from '@/composables/api/use-natural-persons-email.ts'
import { useUserSession } from '@/composables/use-user-session.ts'
import EmailAddressStep from '@/components/ident/steps/personal-data/EmailAddressStep.vue'
import EmailAddressVerifyStep from '@/components/ident/steps/personal-data/EmailAddressVerifyStep.vue'
import Dashboard from '@/components/wrappers/Dashboard.vue'
import Wrapper from '@/components/wrappers/Wrapper.vue'

definePage({
  name: 'edit-email-address',
  meta: {
    titleKey: 'tab.edit_email_address',
    requiresAuth: true,
  },
})

const { t } = useI18n()
const router = useRouter()
const { session } = useUserSession()
const { changeNaturalPersonsEmail } = useNaturalPersonsEmail()
const { confirmNaturalPersonsEmail } = useNaturalPersonsConfirm()

const step = ref<'email' | 'verify'>('email')

async function handleEmailNext(eventData?: StepNavigation<Email>) {
  const personUid = session.value.personUid
  const emailAddress = eventData?.data?.emailAddress
  if (!personUid || !emailAddress) return

  try {
    await changeNaturalPersonsEmail(personUid, { emailAddress })
  } catch {
    return
  }

  step.value = 'verify'
}

async function handleVerifyNext(eventData?: StepNavigation<EmailVerify>) {
  const personUid = session.value.personUid
  const emailConfirmationPIN = eventData?.data?.emailConfirmationPIN
  if (!personUid || !emailConfirmationPIN) return

  try {
    await confirmNaturalPersonsEmail(personUid, { emailConfirmationPIN })
  } catch {
    return
  }

  toast.success(t('settings.edit.success'))
  router.push({ name: 'settings' })
}
</script>
